const SHEET_ID = import.meta.env.VITE_SPREADSHEET_ID;
const TOKEN_URL = import.meta.env.VITE_GRETA_TOKEN_URL;
const CHAT_ID = import.meta.env.VITE_CHAT_ID;
const SHEETS_BASE = 'https://sheets.googleapis.com/v4/spreadsheets';

let cachedToken = {
  token: null,
  expiresAt: 0,
};

let sheetMeta = null;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

async function fetchWithRetry(url, options, attempt = 0) {
  const response = await fetch(url, options);

  if (response.status === 429 && attempt < 3) {
    await sleep(500 * 2 ** attempt);
    return fetchWithRetry(url, options, attempt + 1);
  }

  return response;
}

export async function getAccessToken() {
  if (cachedToken.token && Date.now() < cachedToken.expiresAt) {
    return cachedToken.token;
  }

  const response = await fetch(TOKEN_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      chatId: CHAT_ID,
    }),
  });

  const json = await response.json();

  if (!response.ok || !json.success) {
    throw new Error(json.error || 'Unable to obtain a Google Sheets access token.');
  }

  const expiresIn = Number(json.expires_in || 3600);

  cachedToken = {
    token: json.access_token,
    expiresAt: Date.now() + Math.max(expiresIn - 60, 30) * 1000,
  };

  return cachedToken.token;
}

export async function sheetsRequest(path, init = {}) {
  const token = await getAccessToken();
  const url = `${SHEETS_BASE}/${SHEET_ID}${path}`;

  const response = await fetchWithRetry(url, {
    ...init,
    headers: {
      ...(init.headers || {}),
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Google Sheets API ${response.status}: ${await response.text()}`);
  }

  const text = await response.text();
  return text ? JSON.parse(text) : {};
}

export async function getRows(range) {
  const result = await sheetsRequest(`/values/${encodeURIComponent(range)}`);
  return result.values || [];
}

export async function appendRow(range, values) {
  return sheetsRequest(
    `/values/${encodeURIComponent(range)}:append?valueInputOption=USER_ENTERED&insertDataOption=INSERT_ROWS`,
    {
      method: 'POST',
      body: JSON.stringify({
        values: [values],
      }),
    },
  );
}

export async function updateRow(range, values) {
  return sheetsRequest(
    `/values/${encodeURIComponent(range)}?valueInputOption=USER_ENTERED`,
    {
      method: 'PUT',
      body: JSON.stringify({
        values: [values],
      }),
    },
  );
}

export async function getSheetMeta(force = false) {
  if (!force && sheetMeta) {
    return sheetMeta;
  }

  const result = await sheetsRequest('/?fields=sheets.properties');

  sheetMeta = Object.fromEntries(
    (result.sheets || []).map((sheet) => [
      sheet.properties.title,
      sheet.properties.sheetId,
    ]),
  );

  return sheetMeta;
}

export async function getSheetIdByTitle(title) {
  let meta = await getSheetMeta();

  if (meta[title] !== undefined) {
    return meta[title];
  }

  meta = await getSheetMeta(true);
  return meta[title];
}

function columnLetter(number) {
  let result = '';

  while (number > 0) {
    const remainder = (number - 1) % 26;
    result = String.fromCharCode(65 + remainder) + result;
    number = Math.floor((number - 1) / 26);
  }

  return result;
}

export async function ensureTab(title, headers) {
  let meta = await getSheetMeta();

  if (meta[title] === undefined) {
    await sheetsRequest('/:batchUpdate', {
      method: 'POST',
      body: JSON.stringify({
        requests: [
          {
            addSheet: {
              properties: {
                title,
              },
            },
          },
        ],
      }),
    });

    sheetMeta = null;
    meta = await getSheetMeta(true);
  }

  const lastColumn = columnLetter(headers.length);
  const headerRows = await getRows(`${title}!A1:${lastColumn}1`);
  const existingHeaders = headerRows[0] || [];

  if (!existingHeaders.length || !existingHeaders[0]) {
    await updateRow(`${title}!A1:${lastColumn}1`, headers);
  }
}

export async function findRowIndexById(title, id) {
  const idColumn = await getRows(`${title}!A:A`);

  for (let index = 1; index < idColumn.length; index += 1) {
    if (idColumn[index]?.[0] === id) {
      return index + 1;
    }
  }

  return -1;
}

export async function deleteRow(title, id) {
  const sheetId = await getSheetIdByTitle(title);

  if (sheetId === undefined) {
    throw new Error(`Sheet tab not found: ${title}`);
  }

  const sheetRow = await findRowIndexById(title, id);

  if (sheetRow < 0) {
    throw new Error(`Record not found: ${id}`);
  }

  const startIndex = sheetRow - 1;

  sheetMeta = null;

  return sheetsRequest('/:batchUpdate', {
    method: 'POST',
    body: JSON.stringify({
      requests: [
        {
          deleteDimension: {
            range: {
              sheetId,
              dimension: 'ROWS',
              startIndex,
              endIndex: startIndex + 1,
            },
          },
        },
      ],
    }),
  });
}